import React, { Fragment } from 'react';
import { TranslateFunc } from '@prisjakt/ui-translate/src/translate-func';
import TextLink from '@prisjakt/ui-text-link';
import { PropertyTreeNode, Property, ValueType } from '../types';
import {
  PropertyInterface,
  PropertyBoolean,
  PropertyInterval,
  PropertyLink,
  PropertyList,
  PropertyNumber,
  PropertyString,
} from '../../../types';

const isPropertyType = (
  node: PropertyTreeNode | string | null
): node is PropertyTreeNode => {
  return Boolean((node as PropertyTreeNode).type);
};

export function parseNodes(
  node: PropertyTreeNode | PropertyInterface,
  t: TranslateFunc,
  noChildren = false
) {
  const property: Property = {
    name: node.name || undefined,
    value: {
      type: node.type! as ValueType,
      text: '',
      urls: [],
    },
    byline: node.comment ? node.comment : '',
    children:
      node.children && node.children?.length && !noChildren
        ? (node.children as any[])
            .map(
              child => isPropertyType(child) && parseNodes(child, t, noChildren)
            )
            .filter((child): child is Property => child !== undefined)
        : [],
    column: {},
  };
  switch (node.type) {
    case 'boolean': {
      const value = ((node as unknown) as PropertyBoolean).boolean;

      if (value !== null) {
        property.value!.text = value
          ? t('product', 'property.boolean-yes')
          : t('product', 'property.boolean-no');
        property.value!.value = value;
      }

      return property;
    }

    case 'integer':
    case 'decimal': {
      const value = ((node as unknown) as PropertyNumber).number;
      const unit = node.unit;
      const text = value ?? node.pretty;
      if (text === null || text === undefined) {
        property.value!.text = '';
      } else {
        property.value!.text = unit ? `${text} ${unit}` : String(text);
        property.value!.value = text;
      }

      return property;
    }

    case 'list': {
      const { values, categoryLinks } = (node as unknown) as PropertyList;

      if (!values) {
        return property;
      }

      property.value!.text = (
        <>
          {values.map((value, index) => {
            const link = categoryLinks?.[index];
            const isLastValue = index < values.length - 1;

            if (!value) {
              return null;
            }

            return (
              <Fragment key={value}>
                {link ? <TextLink to={link}>{value}</TextLink> : value}
                {isLastValue ? ', ' : ''}
              </Fragment>
            );
          })}
        </>
      );

      return property;
    }

    case 'interval': {
      const values = [
        ((node as unknown) as PropertyInterval).from,
        ((node as unknown) as PropertyInterval).to,
      ]
        .filter(n => n !== null)
        .filter((value, index, self) => self.indexOf(value) === index)
        .join(' - ');
      const text = values ?? node.pretty;

      property.value!.text = text ? `${text} ${node.unit || ''}`.trim() : '';

      return property;
    }

    case 'link': {
      const { links } = (node as unknown) as PropertyLink;
      property.value!.urls =
        links && links.length
          ? links
              .map(link => link && link.url)
              .filter((link): link is string => !!link)
          : [];
      property.column = { full: true };

      return property;
    }

    case 'string': {
      const {
        value,
        unit,
        categoryLink,
        pretty,
      } = (node as unknown) as PropertyString;
      const text = value ?? pretty;
      if (text === null || text === undefined) {
        property.value!.text = '';
      } else {
        const valueWithUnit = unit ? `${text} ${unit}` : text;
        property.value!.text = categoryLink ? (
          <TextLink to={categoryLink}>{valueWithUnit}</TextLink>
        ) : (
          valueWithUnit
        );
      }

      return property;
    }

    default: {
      return property;
    }
  }
}
